/* global Office */
import { OutlookException } from 'common/exceptions/outlook/OutlookException';
import { OutlookWrongItemSelectedException } from 'common/exceptions/outlook/OutlookWrongItemSelectedException';
import { Attachment } from 'common/models/Attachment';
import { AddInFeatureNotSupportedException } from '../exceptions/application/AddInFeatureNotSupportedException';
import * as Api from '../utils/api';
import { SupportedOfficeItem } from './SupportedOfficeItem';

const TAG = 'OAU3: ';

const DPI = 96;
const POINTS_PER_INCH = 72;
const CM_PER_INCH = 2.54;
const MM_PER_INCH = CM_PER_INCH * 10;

export function getAsyncBody(body: Office.Body) {
  return new Promise<string>((resolve, reject) => {
    body.getAsync('html', (bodyResult) => {
      if (bodyResult.status === Office.AsyncResultStatus.Succeeded)
        resolve(bodyResult.value);
      else
        reject(
          processError('Failed to get outlook item body', bodyResult.error)
        );
    });
  });
}

export function getAsyncRecipients(recipients: Office.Recipients) {
  const promise = new Promise<Office.EmailAddressDetails[]>(function (
    resolve,
    reject
  ) {
    recipients.getAsync((recipientsResult) => {
      if (recipientsResult.status === Office.AsyncResultStatus.Succeeded)
        resolve(recipientsResult.value);
      else
        reject(
          processError(
            'Failed to get outlook item recipients',
            recipientsResult.error
          )
        );
    });
  });

  return promise;
}

export function getAsyncOrganizer(organizer: Office.Organizer) {
  const promise = new Promise<Office.EmailAddressDetails>(function (
    resolve,
    reject
  ) {
    organizer.getAsync((organizerResult) => {
      if (organizerResult.status === Office.AsyncResultStatus.Succeeded)
        resolve(organizerResult.value);
      else
        reject(
          processError(
            'Failed to get outlook item organizer',
            organizerResult.error
          )
        );
    });
  });
  return promise;
}

export function getAsyncSubject(subject: Office.Subject) {
  const promise = new Promise<string>(function (resolve, reject) {
    subject.getAsync((recipientsResult) => {
      if (recipientsResult.status === Office.AsyncResultStatus.Succeeded)
        resolve(recipientsResult.value);
      else
        reject(
          processError(
            'Failed to get outlook item subject',
            recipientsResult.error
          )
        );
    });
  });
  return promise;
}

export function getAsyncTime(time: Office.Time) {
  const promise = new Promise<Date>(function (resolve, reject) {
    time.getAsync((recipientsResult) => {
      if (recipientsResult.status === Office.AsyncResultStatus.Succeeded)
        resolve(recipientsResult.value);
      else
        reject(
          processError(
            'Failed to get outlook item time',
            recipientsResult.error
          )
        );
    });
  });
  return promise;
}

export function saveAsync(appointment: Office.AppointmentCompose) {
  // if API < 1.3
  if (!appointment.saveAsync) {
    throw new AddInFeatureNotSupportedException(
      'Failed to get outlook item id: saving item is not supported in this version of outlook'
    );
  }

  const promise = new Promise<string>(function (resolve, reject) {
    appointment.saveAsync((saveResult) => {
      if (saveResult.status === Office.AsyncResultStatus.Succeeded) {
        resolve(saveResult.value);
      } else {
        // on macOS "Internal Error" will be returned with code 5001
        if (saveResult.error.code == 5001) {
          reject(
            new AddInFeatureNotSupportedException(
              'Failed to get item id: saving item is not supported in MacOS version of outlook'
            )
          );
        }
        reject(processError('Failed to save outlook item', saveResult.error));
      }
    });
  });
  return promise;
}

export function getAsyncId(appointment: Office.AppointmentCompose) {
  // if API < 1.8
  if (!appointment.getItemIdAsync) {
    throw new AddInFeatureNotSupportedException(
      'Failed to get outlook item id: not supported in this version of outlook'
    );
  }

  const promise = new Promise<string>(function (resolve, reject) {
    appointment.getItemIdAsync((getIdResult) => {
      if (getIdResult.status === Office.AsyncResultStatus.Succeeded) {
        resolve(getIdResult.value);
      } else {
        // on macOS "Internal Error" will be returned with code 5001
        // I assume that getItemIdAsync is not supported on Mac, just like the saveAsync
        // although I didn't find any mention of this
        if (getIdResult.error.code == 5001) {
          reject(
            new AddInFeatureNotSupportedException(
              'Failed to get item id: getting item is not supported in MacOS version of outlook'
            )
          );
        }
        reject(
          processError('Failed to get outlook item id', getIdResult.error)
        );
      }
    });
  });
  return promise;
}

export function getAsyncCustomProperties(
  item: SupportedOfficeItem
): Promise<Office.CustomProperties> {
  return new Promise<Office.CustomProperties>((resolve, reject) => {
    const date = new Date();

    console.log(
      TAG,
      `Start loadCustomPropertiesAsync at ${date.toTimeString()}`,
      item.subject
    );

    item.loadCustomPropertiesAsync((asyncResult) => {
      console.log(
        TAG,
        `End loadCustomPropertiesAsync at ${date.toTimeString()}`,
        item.subject
      );

      if (asyncResult.status == Office.AsyncResultStatus.Succeeded)
        resolve(asyncResult.value);
      else
        reject(
          processError(
            'Failed to get outlook item custom properties',
            asyncResult.error
          )
        );
    });
  });
}

export function saveAsyncCustomProperties(
  properties: Office.CustomProperties
): Promise<void> {
  return new Promise<void>(function (resolve, reject) {
    properties.saveAsync((asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Succeeded) resolve();
      else
        reject(
          processError(
            'Failed to save outlook item custom properties',
            asyncResult.error
          )
        );
    });
  });
}

/**
 * Checks if item is a recurring appointment's master item
 * NOTE: this check is supported only on API >= 1.7. On lower versions always returns false
 * Add-in must check this again later using Graph API. This can't be done here because
 * if item wasn't saved yet, we can't retrieve item's ID for Graph API requests
 */
export function isRecurringMaster(item: SupportedOfficeItem): Promise<boolean> {
  return new Promise<boolean>((resolve, reject) => {
    // if NOT appointment OR API < 1.7, return false
    if (!item.recurrence) {
      resolve(false);
    } else if (item.recurrence.recurrenceType && !item.seriesId) {
      resolve(true);
    } else if (!item.recurrence.getAsync) {
      resolve(false);
    } else {
      item.recurrence.getAsync((asyncResult) => {
        if (asyncResult.status == Office.AsyncResultStatus.Succeeded) {
          if (
            asyncResult.value &&
            asyncResult.value.recurrenceType &&
            !item.seriesId
          ) {
            resolve(true);
          } else {
            resolve(false);
          }
        } else {
          reject(
            processError(
              'Failed to check if outlook item is recurring master',
              asyncResult.error
            )
          );
        }
      });
    }
  });
}

export function getSharedProperties(
  item: SupportedOfficeItem
): Promise<Office.SharedProperties | undefined> {
  return new Promise<Office.SharedProperties | undefined>((resolve, reject) => {
    if (!item.getSharedPropertiesAsync) {
      return resolve(undefined);
    }

    item.getSharedPropertiesAsync((asyncResult) => {
      if (asyncResult.status == Office.AsyncResultStatus.Succeeded) {
        resolve(asyncResult.value);
      } else {
        reject(
          processError(
            'Failed to get outlook item shared properties',
            asyncResult.error
          )
        );
      }
    });
  });
}

export function getAttachments(
  item: SupportedOfficeItem
): Promise<(Office.AttachmentDetails | Office.AttachmentDetailsCompose)[]> {
  return new Promise<
    (Office.AttachmentDetails | Office.AttachmentDetailsCompose)[]
  >((resolve, reject) => {
    if (item.attachments) {
      resolve(item.attachments);
    } else if (item.getAttachmentsAsync) {
      item.getAttachmentsAsync((asyncResult) => {
        if (asyncResult.status == Office.AsyncResultStatus.Succeeded) {
          resolve(asyncResult.value);
        } else {
          reject(
            processError(
              'Failed to get outlook item attachments',
              asyncResult.error
            )
          );
        }
      });
    } else {
      reject(
        new AddInFeatureNotSupportedException(
          'Failed to get outlook item attachments: not supported in this version of outlook'
        )
      );
    }
  });
}

export function getAttachmentContent(
  item: SupportedOfficeItem,
  attachmentId: string
): Promise<Office.AttachmentContent> {
  return new Promise<Office.AttachmentContent>((resolve, reject) => {
    if (item.getAttachmentContentAsync) {
      item.getAttachmentContentAsync(attachmentId, (asyncResult) => {
        if (asyncResult.status == Office.AsyncResultStatus.Succeeded) {
          resolve(asyncResult.value);
        } else {
          reject(
            processError(
              `Failed to get outlook item attachment content for attachmentId: ${attachmentId}`,
              asyncResult.error
            )
          );
        }
      });
    } else {
      reject(
        new AddInFeatureNotSupportedException(
          'Failed to get outlook item attachment content: not supported in this version of outlook'
        )
      );
    }
  });
}

function createContentIdRegex(attachment: Attachment) {
  const escapedContentId = attachment.contentId.replace(
    /[.*+?^${}()|[\]\\]/g,
    '\\$&'
  );
  const cidRegex = new RegExp(`src=["']cid:${escapedContentId}["']`, 'gi');
  return cidRegex;
}

export function stripInlineAttachmentCidReferences(
  body: string,
  attachments: Attachment[]
): string {
  let processedBody = body;
  for (const attachment of attachments) {
    if (!attachment.isInline || !attachment.contentId) continue;
    const cidRegex = createContentIdRegex(attachment);
    processedBody = processedBody.replace(cidRegex, 'src=""');
  }
  return processedBody;
}

/**
 * Pulls inline base64 images out of a message body and returns them as attachments.
 *
 * Two limits, and the second one is why this exists:
 *
 *  - `perImageLimitBytes` extracts any single image larger than the limit.
 *  - `totalBudgetBytes` extracts the largest remaining images until what is left inline fits
 *    inside the budget.
 *
 * The per-image limit on its own does not bound the body. Of the 25 largest bodies saved after it
 * shipped, *none* contained an image over the 1 MB threshold — the biggest was 945 KB. They were
 * 8-62 MB because they held 10, 30, sometimes 61 images that each passed the test individually
 * (webapp#3267). Bodies are stored as-is and re-read by the Elasticsearch sync on every reindex,
 * so an unbounded body is a cost paid over and over.
 *
 * Note base64 inflates the encoded size by about 4/3, so a 512 KB budget of decoded image data is
 * roughly 700 KB of body text.
 */
export function extractOversizedBase64Images(
  html: string,
  perImageLimitBytes: number,
  totalBudgetBytes: number = Number.POSITIVE_INFINITY
): { processedHtml: string; extractedImages: Attachment[] } {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const images = doc.querySelectorAll('img[src^="data:"]');
  const extractedImages: Attachment[] = [];

  type Candidate = {
    img: Element;
    contentType: string;
    extension: string;
    base64Data: string;
    byteSize: number;
    documentOrder: number;
  };

  const candidates: Candidate[] = [];

  Array.from(images).forEach((img, documentOrder) => {
    const src = img.getAttribute('src')!;
    const match = src.match(/^data:([^,]+),([\s\S]+)$/);
    if (!match) return;

    const [, meta, base64Data] = match;
    if (!/;base64/i.test(meta)) return;
    const imageType = getUploadableImageType(meta.split(';')[0], base64Data);
    // The upload API rejects extensions it doesn't know and fails the whole save, so an image we
    // can't name stays inline rather than taking the save down with it.
    if (!imageType) return;
    const padding = base64Data.match(/=*$/)?.[0].length ?? 0;
    const byteSize = Math.max(0, Math.floor((base64Data.length * 3) / 4) - padding);

    candidates.push({ img, ...imageType, base64Data, byteSize, documentOrder });
  });

  const toExtract = new Set<Candidate>();

  // Pass 1 — anything over the per-image limit, as before.
  for (const candidate of candidates) {
    if (candidate.byteSize > perImageLimitBytes) {
      toExtract.add(candidate);
    }
  }

  // Pass 2 — keep taking the largest of what is left until the body fits the budget. Largest-first
  // means the fewest images are moved out of the body for a given saving, so a message with one
  // ordinary screenshot keeps it inline and displays unchanged.
  if (Number.isFinite(totalBudgetBytes)) {
    const remaining = candidates
      .filter((c) => !toExtract.has(c))
      .sort((a, b) => b.byteSize - a.byteSize);

    let inlineBytes = remaining.reduce((total, c) => total + c.byteSize, 0);

    for (const candidate of remaining) {
      if (inlineBytes <= totalBudgetBytes) break;
      toExtract.add(candidate);
      inlineBytes -= candidate.byteSize;
    }
  }

  // Emit in document order so the generated names follow the reading order of the message.
  const extracted = candidates
    .filter((c) => toExtract.has(c))
    .sort((a, b) => a.documentOrder - b.documentOrder);

  extracted.forEach((candidate, i) => {
    const index = i + 1;
    candidate.img.removeAttribute('src');
    extractedImages.push({
      id: `extracted-base64-${index}`,
      name: `inline-image-${index}.${candidate.extension}`,
      contentType: candidate.contentType,
      contentBytes: candidate.base64Data,
      size: candidate.byteSize,
      attachmentType: 0,
      isInline: false,
      contentId: '',
      DataType: 'FileAttachment',
    });
  });

  return { processedHtml: doc.body.innerHTML, extractedImages };
}

// Image extensions the file upload API accepts, keyed by MIME subtype.
const UPLOADABLE_IMAGE_EXTENSIONS: Record<string, string> = {
  png: 'png',
  apng: 'apng',
  jpeg: 'jpg',
  jpg: 'jpg',
  pjpeg: 'jpg',
  gif: 'gif',
  bmp: 'bmp',
  webp: 'webp',
  tiff: 'tiff',
};

// Identifies an image from its first bytes, for when the declared type is generic or missing —
// senders often label images application/octet-stream.
function sniffImageSubtype(base64Data: string): string | undefined {
  let header: string;
  try {
    header = atob(base64Data.slice(0, 16));
  } catch {
    return undefined;
  }

  if (header.startsWith('\x89PNG')) return 'png';
  if (header.startsWith('\xFF\xD8\xFF')) return 'jpeg';
  if (header.startsWith('GIF8')) return 'gif';
  if (header.startsWith('RIFF') && header.slice(8, 12) === 'WEBP')
    return 'webp';
  return undefined;
}

function getUploadableImageType(
  declaredType: string,
  base64Data: string
): { contentType: string; extension: string } | null {
  const declaredSubtype = /^image\/([a-z0-9.-]+)/i.exec(declaredType)?.[1];
  const subtype =
    declaredSubtype?.toLowerCase() ?? sniffImageSubtype(base64Data);

  const extension = subtype ? UPLOADABLE_IMAGE_EXTENSIONS[subtype] : undefined;
  if (!extension) {
    return null;
  }
  return { contentType: `image/${subtype}`, extension };
}

export async function updateEmailWithInlineAttachments(
  body: string,
  attachments: Attachment[],
  maxWidthPx: number,
  maxHeightPx: number
): Promise<string> {
  // Get inline attachments with contentId
  const inlineAttachments = attachments.filter(
    (a) => a.isInline && a.contentId
  );

  let processedBody = body;

  // Convert external image URLs to base64, stripping small images
  processedBody = await convertExternalImagesToBase64(
    processedBody,
    maxWidthPx,
    maxHeightPx
  );

  // After external conversion, identify and strip small CID-referenced images
  const smallAttachments = await getSmallInlineAttachments(
    processedBody,
    inlineAttachments,
    maxWidthPx,
    maxHeightPx
  );

  processedBody = stripInlineAttachmentCidReferences(
    processedBody,
    smallAttachments
  );

  const attachmentsToConvert = inlineAttachments.filter(
    (a) => !smallAttachments.includes(a)
  );

  // Replace each remaining CID reference with base64 data URL
  for (const attachment of attachmentsToConvert) {
    const cidRegex = createContentIdRegex(attachment);
    const dataUrl = `data:${attachment.contentType};base64,${attachment.contentBytes}`;
    processedBody = processedBody.replace(cidRegex, `src="${dataUrl}"`);
  }

  return processedBody;
}

async function convertExternalImagesToBase64(
  html: string,
  maxWidthPx: number,
  maxHeightPx: number
): Promise<string> {
  // Create a temporary DOM element to parse HTML
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const images = doc.querySelectorAll('img[src]');

  const conversionPromises: Promise<void>[] = Array.from(images).map((img) =>
    convertExternalImageToBase64(img, maxWidthPx, maxHeightPx)
  );

  await Promise.all(conversionPromises);

  // Serialize the DOM back to HTML
  return doc.body.innerHTML;
}

async function convertExternalImageToBase64(
  img: Element,
  maxWidthPx: number,
  maxHeightPx: number
) {
  const src = img.getAttribute('src');
  if (!src) {
    return;
  }

  // CID references are handled by the separate CID conversion pipeline — skip entirely
  if (/^cid:/i.test(src)) {
    return;
  }

  let { width, height } = getDimensionsFromHtmlElement(img);

  if (width === null || height === null) {
    const native = await loadImageDimensions(src);
    if (native !== null) {
      ({ width, height } = applyNativeDimensionFallback(
        { width, height },
        native
      ));
    }
  }

  if (
    (width !== null && width < maxWidthPx) ||
    (height !== null && height < maxHeightPx)
  ) {
    img.removeAttribute('src');
    return;
  }

  // data: URLs are already base64 — no conversion needed, just leave them
  if (!isExternalImageUrl(src)) {
    return;
  }

  try {
    const base64 = await convertImageToBase64(src);
    if (base64) {
      img.setAttribute('src', base64);
    }
  } catch (error) {
    console.warn('Failed to convert external image to base64:', src, error);
  }
}

function isExternalImageUrl(url: string): boolean {
  // Check if the URL is external (starts with http:// or https://)
  // and is not already a data URL
  return /^https?:\/\//i.test(url) && !url.startsWith('data:');
}

async function convertImageToBase64(url: string): Promise<string | null> {
  try {
    const imageBase64 = await Api.getImageBase64(url);
    return imageBase64;
  } catch (error) {
    console.error('Error converting image to base64:', error);
    return null;
  }
}

function parseCssLength(value: string | null): number | null {
  if (value === null || value.trim() === '') {
    return null;
  }

  const trimmed = value.trim();

  // HTML width/height attributes are often plain numbers (px)
  if (/^\d+(?:\.\d+)?$/.test(trimmed)) {
    return parseFloat(trimmed);
  }

  const match = trimmed.match(/^(\d+(?:\.\d+)?)(px|pt|in|cm|mm)$/i);
  if (!match) {
    return null;
  }

  const num = parseFloat(match[1]);
  const unit = match[2].toLowerCase();

  switch (unit) {
    case 'px':
      return num;
    case 'pt':
      return (num * DPI) / POINTS_PER_INCH;
    case 'in':
      return num * DPI;
    case 'cm':
      return (num * DPI) / CM_PER_INCH;
    case 'mm':
      return (num * DPI) / MM_PER_INCH;
    default:
      return null;
  }
}

function getDimensionsFromHtmlElement(img: Element): {
  width: number | null;
  height: number | null;
} {
  let width = parseCssLength(img.getAttribute('width'));
  let height = parseCssLength(img.getAttribute('height'));

  if (width === null || height === null) {
    const htmlImg = img as HTMLElement;
    if (width === null) width = parseCssLength(htmlImg.style.width);
    if (height === null) height = parseCssLength(htmlImg.style.height);
  }

  return { width, height };
}

function applyNativeDimensionFallback(
  dimensions: { width: number | null; height: number | null },
  native: { width: number; height: number }
): { width: number | null; height: number | null } {
  let { width, height } = dimensions;

  // Avoid divide-by-zero and nonsensical scaling when native dimensions are unavailable.
  if (native.width <= 0 || native.height <= 0) {
    return { width, height };
  }

  if (width === null && height === null) {
    width = native.width;
    height = native.height;
  } else if (width === null && height !== null) {
    width = Math.round((height / native.height) * native.width);
  } else if (height === null && width !== null) {
    height = Math.round((width / native.width) * native.height);
  }

  return { width, height };
}

async function loadImageDimensions(
  src: string
): Promise<{ width: number; height: number } | null> {
  return new Promise<{ width: number; height: number } | null>((resolve) => {
    const img = new Image();
    img.onload = () =>
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

async function getNativeImageDimensions(
  attachment: Attachment
): Promise<{ width: number; height: number } | null> {
  if (!attachment.contentBytes) {
    return null;
  }

  const dataUrl = `data:${attachment.contentType};base64,${attachment.contentBytes}`;
  return loadImageDimensions(dataUrl);
}

async function getSmallInlineAttachments(
  body: string,
  attachments: Attachment[],
  maxWidthPx: number,
  maxHeightPx: number
): Promise<Attachment[]> {
  const parser = new DOMParser();
  const doc = parser.parseFromString(body, 'text/html');
  const imgElements = doc.querySelectorAll('img[src^="cid:"]');

  const result: Attachment[] = [];

  const attachmentsByContentId = new Map(
    attachments
      .filter((a) => a.isInline && a.contentId)
      .map((a) => [a.contentId.toLowerCase(), a])
  );

  for (const img of Array.from(imgElements)) {
    const src = img.getAttribute('src') ?? '';
    const contentId = src.replace(/^cid:/i, '');

    const attachment = attachmentsByContentId.get(contentId.toLowerCase());

    if (!attachment) {
      continue;
    }

    let { width, height } = getDimensionsFromHtmlElement(img);

    if (width === null || height === null) {
      const native = await getNativeImageDimensions(attachment);
      if (native === null) {
        continue;
      }
      ({ width, height } = applyNativeDimensionFallback(
        { width, height },
        native
      ));
    }

    if (
      (width !== null && width < maxWidthPx) ||
      (height !== null && height < maxHeightPx)
    ) {
      result.push(attachment);
    }
  }

  return result;
}

function processError(message: string, error: Office.Error) {
  if (error && error.name === 'GenericResponseError' && error.code === 9020) {
    // see JSDoc for this class
    return new OutlookWrongItemSelectedException(error);
  }
  return new OutlookException(message, error);
}
